import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CodeBlock } from "@/components/CodeBlock";
import { User, FileText, Loader2 } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { useEffect, useState } from "react";

const VIDEO_PENDING = "<!--VPEND-->";

const VideoPendingBar = () => {
  const [pct, setPct] = useState(6);
  useEffect(() => {
    const start = Date.now();
    const est = 180000; // ~3 min estimate
    const t = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(92, (elapsed / est) * 100);
      setPct(6 + p * 0.9);
    }, 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="my-2 max-w-md" data-testid="video-pending-bar">
      <div className="flex items-center gap-2 text-sm text-zinc-300 mb-2">
        <Loader2 className="w-4 h-4 text-[#00FF66] animate-spin" />
        Generating your video — this can take 2–3 minutes 🎬
      </div>
      <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full bg-[#00FF66] rounded-full transition-all duration-1000 ease-linear"
          style={{ width: `${pct}%`, boxShadow: "0 0 12px rgba(0,255,102,0.5)" }}
        />
      </div>
      <p className="text-xs text-zinc-500 mt-1.5">It'll appear here automatically when ready.</p>
    </div>
  );
};

const Markdown = ({ content }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      code({ inline, className, children, ...props }) {
        const match = /language-(\w+)/.exec(className || "");
        const value = String(children).replace(/\n$/, "");
        if (!inline && (match || value.includes("\n"))) {
          return <CodeBlock language={match ? match[1] : ""} value={value} />;
        }
        return (
          <code className="font-mono text-[0.85em] text-[#00FF66] bg-white/10 px-1.5 py-0.5 rounded" {...props}>
            {children}
          </code>
        );
      },
      p: ({ children }) => <p className="mb-3 leading-relaxed last:mb-0">{children}</p>,
      ul: ({ children }) => <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol>,
      li: ({ children }) => <li className="leading-relaxed">{children}</li>,
      h1: ({ children }) => <h1 className="font-heading font-bold text-2xl mb-3 mt-4 text-white">{children}</h1>,
      h2: ({ children }) => <h2 className="font-heading font-bold text-xl mb-2 mt-4 text-white">{children}</h2>,
      h3: ({ children }) => <h3 className="font-heading font-semibold text-lg mb-2 mt-3 text-white">{children}</h3>,
      a: ({ children, href }) => (
        <a href={href} target="_blank" rel="noreferrer" className="text-[#00FF66] underline hover:text-[#00CC52]">
          {children}
        </a>
      ),
      blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-[#00FF66] pl-4 italic text-zinc-400 my-3">{children}</blockquote>
      ),
      table: ({ children }) => (
        <div className="overflow-x-auto my-3">
          <table className="w-full text-sm border border-white/10">{children}</table>
        </div>
      ),
      th: ({ children }) => <th className="border border-white/10 px-3 py-2 bg-[#141415] text-left">{children}</th>,
      td: ({ children }) => <td className="border border-white/10 px-3 py-2">{children}</td>,
    }}
  >
    {content}
  </ReactMarkdown>
);

const MEDIA_RE = /::media:(image|audio|video)::(.+?)::(.+?)::/;

const MediaFallback = ({ name }) => (
  <div className="my-3 rounded-xl border border-white/10 bg-[#141415] px-4 py-3 text-sm text-zinc-400" data-testid="chat-media-error">
    This file ({name}) is no longer available. Please ask me to generate it again.
  </div>
);

const MediaBlock = ({ kind, url, name }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return <MediaFallback name={name} />;
  if (kind === "image") {
    return (
      <div className="my-3" data-testid="chat-media-image">
        <img src={url} alt={name} onError={() => setFailed(true)} className="max-w-full sm:max-w-md rounded-xl border border-white/10" />
        <a href={url} download={name} className="inline-block mt-2 text-sm text-[#00FF66] underline hover:text-[#00CC52]" data-testid="chat-media-download">
          Download {name}
        </a>
      </div>
    );
  }
  if (kind === "audio") {
    return (
      <div className="my-3" data-testid="chat-media-audio">
        <audio controls src={url} onError={() => setFailed(true)} className="w-full sm:max-w-md" />
        <a href={url} download={name} className="block mt-2 text-sm text-[#00FF66] underline hover:text-[#00CC52]" data-testid="chat-media-download">
          Download {name}
        </a>
      </div>
    );
  }
  return (
    <div className="my-3" data-testid="chat-media-video">
      <video controls src={url} onError={() => setFailed(true)} className="max-w-full sm:max-w-lg rounded-xl border border-white/10 bg-black" />
      <a href={url} download={name} className="block mt-2 text-sm text-[#00FF66] underline hover:text-[#00CC52]" data-testid="chat-media-download">
        Download {name}
      </a>
    </div>
  );
};

const AssistantContent = ({ content }) => {
  const parts = [];
  let rest = content || "";
  let m;
  while ((m = rest.match(MEDIA_RE))) {
    const before = rest.slice(0, m.index);
    if (before.trim()) parts.push({ type: "md", value: before });
    parts.push({ type: "media", kind: m[1], url: m[2], name: m[3] });
    rest = rest.slice(m.index + m[0].length);
  }
  if (rest.trim()) parts.push({ type: "md", value: rest });
  return (
    <>
      {parts.map((p, i) =>
        p.type === "media" ? (
          <MediaBlock key={i} kind={p.kind} url={p.url} name={p.name} />
        ) : (
          <Markdown key={i} content={p.value} />
        )
      )}
    </>
  );
};

export const ChatMessage = ({ role, content, images, docNames, streaming }) => {
  const isUser = role === "user";

  if (isUser) {
    return (
      <div className="flex justify-end gap-3 animate-fade-in-up" data-testid="chat-message-user">
        <div className="max-w-[80%]">
          {images && images.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2 justify-end">
              {images.map((img, i) => (
                <img key={i} src={img} alt="upload" className="w-28 h-28 object-cover rounded-lg border border-white/10" />
              ))}
            </div>
          )}
          {docNames && docNames.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2 justify-end" data-testid="user-doc-chips">
              {docNames.map((n, i) => (
                <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-white/10 bg-[#0A0A0A] max-w-[220px]">
                  <FileText className="w-4 h-4 text-[#00FF66] shrink-0" />
                  <span className="text-xs text-zinc-300 truncate">{n}</span>
                </div>
              ))}
            </div>
          )}
          <div className="bg-[#1F1F22] rounded-2xl rounded-tr-sm px-4 py-3 text-zinc-100 whitespace-pre-wrap break-words">
            {content}
          </div>
        </div>
        <div className="w-8 h-8 rounded-lg bg-[#1F1F22] border border-white/10 flex items-center justify-center shrink-0 mt-1">
          <User className="w-4 h-4 text-zinc-400" />
        </div>
      </div>
    );
  }

  const isPendingVideo = (content || "").includes(VIDEO_PENDING);

  return (
    <div className="flex gap-3 animate-fade-in-up" data-testid="chat-message-assistant">
      <LogoMark className="w-8 h-8 shrink-0 mt-1" />
      <div className="flex-1 min-w-0 text-zinc-200 pt-1">
        {isPendingVideo ? (
          <VideoPendingBar />
        ) : (
          <>
            <AssistantContent content={content || ""} />
            {streaming && <span className="inline-block w-2 h-4 bg-[#00FF66] ml-0.5 cursor-blink align-middle" />}
          </>
        )}
      </div>
    </div>
  );
};
